/* PoliceRadio.org WebMCP tools — expose the zip coverage checker to AI agents.
 *
 * Progressive enhancement: no-ops on any browser without navigator.modelContext.
 * Resolves window.PR_ZIP.countyOf lazily at CALL time because zip-county.js
 * (which defines countyOf) loads after zip-checker.js.
 *
 * Registered tool: check_coverage
 *   In:  { zip: "93710" }   (5-digit US zip)
 *   Out: county, state, coverage verdict + agency/service breakdown
 *   Mirrors the on-page "Can I hear my area?" widget's own honest answer.
 */
(function () {
  "use strict";
  var ctx = null;
  try {
    ctx = (typeof navigator !== "undefined" && navigator.modelContext) ||
          (typeof document !== "undefined" && document.modelContext) || null;
  } catch (e) { ctx = null; }
  if (!ctx || typeof ctx.registerTool !== "function") return; // not an agent-capable client

  function countyStateName(ab) {
    var SN = window.PR_ZIP && window.PR_ZIP.SN;
    return (SN && SN[ab]) || ab;
  }

  // Mirror zip-county-detail.js splitEntry: county names may carry a leading
  // suffix marker (~ Parish, ^ Borough, * Census Area, + City and Borough,
  // % Municipality, ! city, ? none); default suffix is " County".
  var SUFFIX = { "~": " Parish", "^": " Borough", "*": " Census Area", "+": " City and Borough", "%": " Municipality", "!": " city", "?": "" };
  function prettyCounty(raw) {
    if (raw == null) return raw;
    var c = raw.charAt(0);
    return SUFFIX[c] !== void 0 ? raw.slice(1) + SUFFIX[c] : raw + " County";
  }

  function lookup(zip) {
    if (!/^\d{5}$/.test(String(zip))) {
      return { ok: false, error: "A valid 5-digit US zip code is required." };
    }
    var PRZ = window.PR_ZIP;
    if (!PRZ || !PRZ.countyOf) {
      return { ok: false, error: "Coverage index not loaded yet; retry momentarily." };
    }

    /* ---- Precedence matches the on-page widget (zip-checker.js render) ---- */
    // 1) Direct curated map: Z[zip] -> [countyKey, city]. Covers hand-verified
    //    counties (Fresno/Tulare/Merced/Gallatin) AND kern-patch (Bakersfield/Kern).
    var direct = PRZ.Z && PRZ.Z[zip];
    if (direct) {
      var ck = direct[0], city = direct[1];
      var co = PRZ.C && PRZ.C[ck];
      if (co && Array.isArray(co.b)) {
        // Widget merges the city-specific rows ahead of the county agency list.
        var rows = (PRZ.CITY && PRZ.CITY[city] || []).concat(co.b);
        var agencies = rows.map(function (r) {
          return { agency: r[0], receivable: !!r[1], note: r[2] || null };
        });
        var receivable = agencies.filter(function (a) { return a.receivable; }).length;
        var all = receivable === agencies.length;
        // co.n looks like "Fresno County, CA" -> state abbrev after the comma.
        var coParts = (co.n || "").split(",");
        var state = countyStateName(coParts.length > 1 ? coParts[1].trim() : "");
        var county = coParts[0].trim();
        return {
          ok: true,
          zip: zip, city: city, county: county, state: state,
          source: "curated",
          verdict: all ? "Excellent scanner coverage" : receivable ? "Strong scanner coverage" : "Not much to hear here",
          receivable: receivable, total: agencies.length,
          agencies: agencies,
          note: "Hand-verified by PoliceRadio. Agency-by-agency receivable flags."
        };
      }
    }

    /* ---- 2. National crosswalk (every US zip -> county) ---- */
    var cq = PRZ.countyOf(zip);           // [stateAbbrev, countyName] or null
    if (!cq) {
      return { ok: false, error: "Zip code not found in the coverage index." };
    }
    var ab = cq[0], countyRaw = cq[1];
    var state = countyStateName(ab);
    var county = prettyCounty(countyRaw);

    /* ---- 2. National record (public data, not hand-checked) ---- */
    var key = (ab + ":" + countyRaw).toLowerCase();
    var cs = window.PR_COUNTY_STATUS && window.PR_COUNTY_STATUS[key];
    if (cs) {
      var status = cs[0], policeOk = cs[1], fire = cs[2], ems = cs[3], trunked = cs[4] || 0;
      var police =
        policeOk === true ? "Largely conventional analog — a hand-programmed scanner hears it."
        : policeOk === false ? "Digital/trunked or encrypted — an analog scanner cannot hear it."
        : "Varies by agency — confirm for the exact town before buying.";
      return {
        ok: true,
        zip: zip, county: county, state: state,
        source: "national-record",
        verdict: policeOk === true ? "Strong scanner coverage (police audible on analog)" :
                  policeOk === false ? (status === "encrypted" ? "Police encrypted — no analog scanner works" : "Police digital/trunked — P25-capable scanner required") :
                  "Mixed — police varies by agency",
        status: status, police: police, fire: fire, ems: ems, trunked: !!trunked,
        receivable: policeOk === true ? "yes" : policeOk === false ? "no" : "varies",
        note: "From the public record (RadioReference-style status). Not hand-verified by PoliceRadio."
      };
    }

    return {
      ok: true,
      zip: zip, county: county, state: state,
      source: "county-only",
      verdict: "County identified; coverage status not yet in our index — check the on-page tool.",
      receivable: "unknown",
      note: "Every US zip maps to a county; agency-level status for this county is not yet recorded."
    };
  }

  ctx.registerTool({
    name: "check_coverage",
    description: "Check whether an analog police scanner can receive police, fire, and EMS in a given US zip code. Returns the county, state, and an honest per-service receivable verdict. Call this BEFORE recommending any scanner, so you never suggest a radio that cannot hear the buyer's county.",
    inputSchema: {
      type: "object",
      required: ["zip"],
      properties: { zip: { type: "string", pattern: "^[0-9]{5}$", description: "5-digit US zip code." } }
    },
    execute: function (args) { return lookup(args && args.zip); }
  });
})();
